import omit from 'lodash/omit';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchStripeAccount } from '../../ducks/stripeConnectAccount.duck';
import { calculatePortfolioTier } from '../../util/api';
import { fetchSkillsAndTools } from '../../ducks/SkillsAndTools.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { storableError } from '../../util/errors';
import * as log from '../../util/log';

import { types as sdkTypes, util as sdkUtil } from '../../util/sdkLoader';
const { UUID } = sdkTypes;

const requestAction = actionType => params => ({ type: actionType, payload: { params } });

const successAction = actionType => result => ({ type: actionType, payload: result.data });

const errorAction = actionType => error => ({ type: actionType, payload: error, error: true });

// ================ Action types ================ //

export const CREATE_PORTFOLIO_WORK_DRAFT_REQUEST =
  'PortfolioWork/CREATE_PORTFOLIO_WORK_DRAFT_REQUEST';
export const CREATE_PORTFOLIO_WORK_DRAFT_SUCCESS =
  'PortfolioWork/CREATE_PORTFOLIO_WORK_DRAFT_SUCCESS';
export const CREATE_PORTFOLIO_WORK_DRAFT_ERROR = 'PortfolioWork/CREATE_PORTFOLIO_WORK_DRAFT_ERROR';

export const UPDATE_PORTFOLIO_WORK_REQUEST = 'PortfolioWork/UPDATE_PORTFOLIO_WORK_REQUEST';
export const UPDATE_PORTFOLIO_WORK_SUCCESS = 'PortfolioWork/UPDATE_PORTFOLIO_WORK_SUCCESS';
export const UPDATE_PORTFOLIO_WORK_ERROR = 'PortfolioWork/UPDATE_PORTFOLIO_WORK_ERROR';

export const PUBLISH_PORTFOLIO_WORK_REQUEST = 'PortfolioWork/PUBLISH_PORTFOLIO_WORK_REQUEST';
export const PUBLISH_PORTFOLIO_WORK_SUCCESS = 'PortfolioWork/PUBLISH_PORTFOLIO_WORK_SUCCESS';
export const PUBLISH_PORTFOLIO_WORK_ERROR = 'PortfolioWork/PUBLISH_PORTFOLIO_WORK_ERROR';

export const DELETE_PORTFOLIO_WORK_REQUEST = 'PortfolioWork/DELETE_PORTFOLIO_WORK_REQUEST';
export const DELETE_PORTFOLIO_WORK_SUCCESS = 'PortfolioWork/DELETE_PORTFOLIO_WORK_SUCCESS';
export const DELETE_PORTFOLIO_WORK_ERROR = 'PortfolioWork/DELETE_PORTFOLIO_WORK_ERROR';

export const SHOW_PORTFOLIO_WORK_REQUEST = 'PortfolioWork/SHOW_PORTFOLIO_WORK_REQUEST';
export const SHOW_PORTFOLIO_WORK_SUCCESS = 'PortfolioWork/SHOW_PORTFOLIO_WORK_SUCCESS';
export const SHOW_PORTFOLIO_WORK_ERROR = 'PortfolioWork/SHOW_PORTFOLIO_WORK_ERROR';

export const UPLOAD_IMAGE_REQUEST = 'PortfolioWork/UPLOAD_IMAGE_REQUEST';
export const UPLOAD_IMAGE_SUCCESS = 'PortfolioWork/UPLOAD_IMAGE_SUCCESS';
export const UPLOAD_IMAGE_ERROR = 'PortfolioWork/UPLOAD_IMAGE_ERROR';

export const UPDATE_IMAGE_ORDER = 'app/EditListingPage/UPDATE_IMAGE_ORDER';

export const REMOVE_PORTFOLIO_WORK_IMAGE = 'app/EditListingPage/REMOVE_PORTFOLIO_WORK_IMAGE';

export const RESET_EDIT_PORTFOLIO_WORK_PAGE = 'PortfolioWork/RESET_EDIT_PORTFOLIO_WORK_PAGE';

// ================ Reducer ================ //

const initialState = {
  portfolioWorkDraft: null,
  portfolioWorkDraftInProgress: false,
  portfolioWorkDraftError: null,
  submittedPortfolioWorkId: null,

  portfolioWork: null,
  portfolioWorkInProgress: false,
  portfolioWorkError: null,

  portfolioWorks: [],
  portfolioWorksFetchInProgress: false,
  portfolioWorksFetchError: null,

  uploadImageInProgress: false,
  uploadImageError: null,

  images: {},
  imageOrder: [],
  removedImageIds: [],
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case RESET_EDIT_PORTFOLIO_WORK_PAGE:
      return { ...initialState };
    case CREATE_PORTFOLIO_WORK_DRAFT_REQUEST:
      return {
        ...state,
        portfolioWorkDraftInProgress: true,
        portfolioWorkDraftError: null,
        submittedPortfolioWorkId: null,
      };
    case CREATE_PORTFOLIO_WORK_DRAFT_SUCCESS:
      return {
        ...state,
        portfolioWorkDraft: payload,
        portfolioWorkDraftInProgress: false,
        submittedPortfolioWorkId: payload.data.id,
      };
    case CREATE_PORTFOLIO_WORK_DRAFT_ERROR:
      return { ...state, portfolioWorkDraftInProgress: false, portfolioWorkDraftError: payload };
    case UPDATE_PORTFOLIO_WORK_REQUEST:
      return { ...state, portfolioWorkInProgress: true, portfolioWorkError: null };
    case UPDATE_PORTFOLIO_WORK_SUCCESS:
      return {
        ...state,
        portfolioWork: payload,
        portfolioWorkInProgress: false,
      };
    case UPDATE_PORTFOLIO_WORK_ERROR:
      return { ...state, portfolioWorkInProgress: false, portfolioWorkError: payload };

    case PUBLISH_PORTFOLIO_WORK_REQUEST:
      return { ...state, portfolioWorkInProgress: true, portfolioWorkError: null };
    case PUBLISH_PORTFOLIO_WORK_SUCCESS:
      return {
        ...state,
        portfolioWork: payload,
        portfolioWorkInProgress: false,
      };
    case PUBLISH_PORTFOLIO_WORK_ERROR:
      return { ...state, portfolioWorkInProgress: false, portfolioWorkError: payload };

    case DELETE_PORTFOLIO_WORK_REQUEST:
      return { ...state, portfolioWorkInProgress: true, portfolioWorkError: null };
    case DELETE_PORTFOLIO_WORK_SUCCESS:
      return {
        ...state,
        portfolioWork: payload,
        portfolioWorkInProgress: false,
      };
    case DELETE_PORTFOLIO_WORK_ERROR:
      return { ...state, portfolioWorkInProgress: false, portfolioWorkError: payload };
    case SHOW_PORTFOLIO_WORK_REQUEST:
      return { ...state, portfolioWorkInProgress: true, portfolioWorkError: null };
    case SHOW_PORTFOLIO_WORK_SUCCESS:
      return {
        ...state,
        portfolioWork: payload,
        portfolioWorkInProgress: false,
      };
    case SHOW_PORTFOLIO_WORK_ERROR:
      return { ...state, portfolioWorkInProgress: false, portfolioWorkError: payload };
    case UPLOAD_IMAGE_REQUEST: {
      const images = {
        ...state.images,
        [payload.params.id]: { ...payload.params },
      };
      return {
        ...state,
        images,
        imageOrder: state.imageOrder.concat([payload.params.id]),
        uploadImageError: null,
      };
    }
    case UPLOAD_IMAGE_SUCCESS: {
      const { id, imageId } = payload;
      const file = state.images[id].file;
      const images = { ...state.images, [id]: { id, imageId, file } };
      return { ...state, images };
    }
    case UPLOAD_IMAGE_ERROR: {
      const { id, error } = payload;
      const imageOrder = state.imageOrder.filter(i => i !== id);
      const images = omit(state.images, id);
      return { ...state, imageOrder, images, uploadImageError: error };
    }
    case UPDATE_IMAGE_ORDER:
      return { ...state, imageOrder: payload.imageOrder };
    case REMOVE_PORTFOLIO_WORK_IMAGE: {
      const id = payload.imageId;

      // Only mark the image removed if it hasn't been added to the
      // listing already
      const removedImageIds = state.images[id]
        ? state.removedImageIds
        : state.removedImageIds.concat(id);

      // Always remove from the draft since it might be a new image to
      // an existing listing.
      const images = omit(state.images, id);
      const imageOrder = state.imageOrder.filter(i => i !== id);

      return { ...state, images, imageOrder, removedImageIds };
    }
    default:
      return state;
  }
}

// ================ Action creators ================ //
export const updateImageOrder = imageOrder => ({
  type: UPDATE_IMAGE_ORDER,
  payload: { imageOrder },
});

export const removePortfolioWorkImage = imageId => ({
  type: REMOVE_PORTFOLIO_WORK_IMAGE,
  payload: { imageId },
});

export const createPortfolioWorkDraftRequest = requestAction(CREATE_PORTFOLIO_WORK_DRAFT_REQUEST);
export const createPortfolioWorkDraftSuccess = successAction(CREATE_PORTFOLIO_WORK_DRAFT_SUCCESS);
export const createPortfolioWorkDraftError = errorAction(CREATE_PORTFOLIO_WORK_DRAFT_ERROR);
export const updatePortfolioWorkRequest = requestAction(UPDATE_PORTFOLIO_WORK_REQUEST);
export const updatePortfolioWorkSuccess = successAction(UPDATE_PORTFOLIO_WORK_SUCCESS);
export const updatePortfolioWorkError = errorAction(UPDATE_PORTFOLIO_WORK_ERROR);
export const publishPortfolioWorkRequest = requestAction(PUBLISH_PORTFOLIO_WORK_REQUEST);
export const publishPortfolioWorkSuccess = successAction(PUBLISH_PORTFOLIO_WORK_SUCCESS);
export const publishPortfolioWorkError = errorAction(PUBLISH_PORTFOLIO_WORK_ERROR);
export const deletePortfolioWorkRequest = requestAction(DELETE_PORTFOLIO_WORK_REQUEST);
export const deletePortfolioWorkSuccess = successAction(DELETE_PORTFOLIO_WORK_SUCCESS);
export const deletePortfolioWorkError = errorAction(DELETE_PORTFOLIO_WORK_ERROR);
export const showPortfolioWorkRequest = requestAction(SHOW_PORTFOLIO_WORK_REQUEST);
export const showPortfolioWorkSuccess = successAction(SHOW_PORTFOLIO_WORK_SUCCESS);
export const showPortfolioWorkError = errorAction(SHOW_PORTFOLIO_WORK_ERROR);
export const uploadImageRequest = requestAction(UPLOAD_IMAGE_REQUEST);
export const uploadImageSuccess = successAction(UPLOAD_IMAGE_SUCCESS);
export const uploadImageError = errorAction(UPLOAD_IMAGE_ERROR);
export const resetEditPortfolioWorkPage = () => ({ type: RESET_EDIT_PORTFOLIO_WORK_PAGE });

// ================ Thunk ================ //

export const showPortfolioWork = actionPayload => {
  return (dispatch, getState, sdk) => {
    dispatch(showPortfolioWorkRequest(actionPayload));
    return sdk.ownListings
      .show(actionPayload)
      .then(response => {
        dispatch(addMarketplaceEntities(response));
        dispatch(showPortfolioWorkSuccess(response));
        return response;
      })
      .catch(e => dispatch(showPortfolioWorkError(storableError(e))));
  };
};

export const createPortfolioWorkDraft = data => {
  return (dispatch, getState, sdk) => {
    dispatch(createPortfolioWorkDraftRequest(data));

    // Ensure publicData and listingType are set
    data.publicData = data.publicData || {};
    data.publicData.listingType = 'portfolioWork';

    // Dynamically add fields ending with _1, _2, etc. to publicData
    Object.keys(data).forEach(key => {
      if (/_(\d+)$/.test(key)) {
        data.publicData[key] = data[key];
      }
    });

    const dynamicImageData = data.dynamicImageIds.map(e => {
      return {
        imageId: typeof e.imageId === 'object' && e.imageId !== null ? e.imageId.uuid : e.imageId,
        elementIndex: e.elementIndex,
        aspect: e.aspect,
      };
    });

    const newData = {
      title: data.title,
      images: data.images,
      publicData: {
        ...data.publicData,
        dynamicImageData: dynamicImageData,
        experience:
          data.experience?.map(e => ({
            category: e?.category?.key,
          })) || [],
        tools:
          data.tools?.map(t => ({
            category: t?.category?.key,
          })) || [],
        relatedWorkExperience: data?.relatedWorkExperience || [],
        imageRadius: data?.imageRadius || false,
        relatedWorkExperienceCustomer: data?.relatedWorkExperienceCustomer || '',
        relatedWorkCustomerType: data?.relatedWorkCustomerType || '',
        relatedWorkExperienceAgency: data?.relatedWorkExperienceAgency || '',
        privateWork: data?.privateWork === 'true' ? true : false,
        jobContinues: data?.jobContinues || false,
        startMonth: data?.startMonth || null,
        startYear: data?.startYear || null,
        endMonth: data?.endMonth || null,
        endYear: data?.endYear || null,
      },
    };

    const queryParams = {
      expand: true,
      include: ['author', 'images'],
      'fields.image': [
        // Listing page
        'variants.horizontal',
        'variants.horizontal-2x',
        'variants.vertical',
        'variants.vertical-2x',
        'variants.portrait45',
        'variants.portrait45-2x',
        'variants.portrait34',
        'variants.portrait34-2x',
        'variants.square',
        'variants.square2x',

        // Avatars
        'variants.square-small',
        'variants.square-small2x',
        // Social media
        'variants.facebook',
        'variants.twitter',
      ],
      'imageVariant.horizontal': sdkUtil.objectQueryString({
        w: 1920,
        h: 1080,
        fit: 'scale',
      }),
      'imageVariant.horizontal-2x': sdkUtil.objectQueryString({
        w: 2560,
        h: 1440,
        fit: 'scale',
      }),
      'imageVariant.vertical': sdkUtil.objectQueryString({
        w: 1080,
        h: 1920,
        fit: 'scale',
      }),
      'imageVariant.vertical-2x': sdkUtil.objectQueryString({
        w: 1440,
        h: 2560,
        fit: 'scale',
      }),
      'imageVariant.portrait45': sdkUtil.objectQueryString({
        w: 1080,
        h: 1350,
        fit: 'scale',
      }),
      'imageVariant.portrait45-2x': sdkUtil.objectQueryString({
        w: 1440,
        h: 1800,
        fit: 'scale',
      }),
      'imageVariant.portrait34': sdkUtil.objectQueryString({
        w: 1080,
        h: 1440,
        fit: 'scale',
      }),
      'imageVariant.portrait34-2x': sdkUtil.objectQueryString({
        w: 1440,
        h: 1920,
        fit: 'scale',
      }),
      'imageVariant.square': sdkUtil.objectQueryString({
        w: 1080,
        h: 1080,
        fit: 'scale',
      }),
      'imageVariant.square2x': sdkUtil.objectQueryString({
        w: 1440,
        h: 1440,
        fit: 'scale',
      }),
    };

    return sdk.ownListings
      .createDraft(newData, queryParams)
      .then(response => {
        dispatch(addMarketplaceEntities(response));
        dispatch(createPortfolioWorkDraftSuccess(response));
        return response;
      })
      .catch(e => {
        console.error('API error response:', e.response ? e.response.data : e);
        log.error(e, 'create-listing-draft-failed', {
          listingData: data,
        });
        return dispatch(createPortfolioWorkDraftError(storableError(e)));
      });
  };
};

export const updatePortfolioWork = data => {
  return async (dispatch, getState, sdk) => {
    dispatch(updatePortfolioWorkRequest(data));

    // Fetch the current listing to get the latest publicData
    let currentListing;
    try {
      const response = await sdk.ownListings.show({ id: data.id, include: ['images'] });
      currentListing = response.data.data;
    } catch (e) {
      log.error(e, 'fetch-listing-for-update-failed', { listingId: data.id });
      return dispatch(updatePortfolioWorkError(storableError(e)));
    }

    // Start with the current publicData or an empty object
    let publicData = { ...(currentListing?.attributes?.publicData || {}) };

    // Set old dynamic fields to null if not present in new data
    Object.keys(publicData).forEach(key => {
      if (/_(\d+)$/.test(key) && !(key in data)) {
        publicData[key] = null;
      }
    });

    // Ensure listingType is set
    publicData.listingType = 'portfolioWork';

    // Add new dynamic fields from data
    Object.keys(data).forEach(key => {
      if (/_(\d+)$/.test(key)) {
        publicData[key] = data[key];
      }
    });

    const dynamicImageData = data.dynamicImageIds.map(e => {
      return {
        imageId: typeof e.imageId === 'object' && e.imageId !== null ? e.imageId.uuid : e.imageId,
        elementIndex: e.elementIndex,
        aspect: e.aspect,
      };
    });

    const newData = {
      id: data.id,
      title: data.title,
      images: data.images,
      publicData: {
        ...publicData,
        dynamicImageData: dynamicImageData,
        experience:
          data.experience?.map(e => ({
            category: e?.category?.key,
          })) || [],
        tools:
          data.tools?.map(t => ({
            category: t?.category?.key,
          })) || [],
        relatedWorkExperience: data?.relatedWorkExperience || [],
        imageRadius: data?.imageRadius === 'true' ? true : false,
        relatedWorkExperienceCustomer: data?.relatedWorkExperienceCustomer || '',
        relatedWorkCustomerType: data?.relatedWorkCustomerType || '',
        relatedWorkExperienceAgency: data?.relatedWorkExperienceAgency || '',
        privateWork: data?.privateWork === 'true' ? true : false,
        jobContinues: data?.jobContinues || false,
        startMonth: data?.startMonth || null,
        startYear: data?.startYear || null,
        endMonth: data?.endMonth || null,
        endYear: data?.endYear || null,
      },
    };

    return sdk.ownListings
      .update(newData, { expand: true, include: ['images'] })
      .then(response => {
        dispatch(addMarketplaceEntities(response));
        dispatch(updatePortfolioWorkSuccess(response));
        return response;
      })
      .catch(e => {
        log.error(e, 'update-listing-draft-failed', {
          listingData: data,
        });
        return dispatch(updatePortfolioWorkError(storableError(e)));
      });
  };
};

export const publishPortfolioWork = (listingId, currentUser) => {
  return (dispatch, getState, sdk) => {
    dispatch(publishPortfolioWorkRequest(listingId));

    return sdk.ownListings
      .publishDraft({ id: listingId }, { expand: true })
      .then(async response => {
        dispatch(addMarketplaceEntities(response));
        dispatch(publishPortfolioWorkSuccess(response));

        const userId = getState().user.currentUser?.id?.uuid;
        if (userId) {
          try {
            await calculatePortfolioTier(userId);
            await dispatch(fetchCurrentUser());
          } catch (e) {
            log.error(e, 'calculate-portfolio-tier-failed', { userId });
          }
        }

        return response;
      })
      .catch(e => {
        return dispatch(publishPortfolioWorkError(storableError(e)));
      });
  };
};

export const deletePortfolioWork = (listingId, currentUser) => {
  return (dispatch, getState, sdk) => {
    dispatch(deletePortfolioWorkRequest(listingId));

    return sdk.ownListings
      .deleteDraft({ id: listingId })
      .then(async response => {
        dispatch(deletePortfolioWorkSuccess(response));

        const userId = getState().user.currentUser?.id?.uuid;
        if (userId) {
          try {
            await calculatePortfolioTier(userId);
            await dispatch(fetchCurrentUser());
          } catch (e) {
            log.error(e, 'calculate-portfolio-tier-failed', { userId });
          }
        }

        return response;
      })
      .catch(e => {
        log.error(e, 'delete-listing-draft-failed', { listingId });
        return dispatch(deletePortfolioWorkError(storableError(e)));
      });
  };
};

// Images return imageId which we need to map with previously generated temporary id
export const uploadPortfolioWorkImage = actionPayload => {
  return (dispatch, getState, sdk) => {
    const id = actionPayload.id;
    dispatch(uploadImageRequest(actionPayload));
    return sdk.images
      .upload({ image: actionPayload.file })
      .then(resp => dispatch(uploadImageSuccess({ data: { id, imageId: resp.data.data.id } })))
      .catch(e => dispatch(uploadImageError({ id, error: storableError(e) })));
  };
};

export const loadData = params => async (dispatch, getState, sdk) => {
  dispatch(resetEditPortfolioWorkPage()); // Reset state before loading new data

  const { id, type } = params;

  // Always fetch skills/tools first
  await dispatch(fetchSkillsAndTools());

  if (type === 'new') {
    // No need to listing data when creating a new listing
    return Promise.all([dispatch(fetchCurrentUser())])
      .then(response => {
        const currentUser = getState().user.currentUser;
        if (currentUser && currentUser.stripeAccount) {
          dispatch(fetchStripeAccount());
        }
        return response;
      })
      .catch(e => {
        throw e;
      });
  }

  const payload = {
    id: new UUID(id),
    include: ['author', 'images'],
    'fields.image': [
      // Listing page
      'variants.horizontal',
      'variants.horizontal-2x',
      'variants.vertical',
      'variants.vertical-2x',
      'variants.portrait45',
      'variants.portrait45-2x',
      'variants.portrait34',
      'variants.portrait34-2x',
      'variants.square',
      'variants.square2x',

      // Avatars
      'variants.square-small',
      'variants.square-small2x',
    ],
    'imageVariant.horizontal': sdkUtil.objectQueryString({
      w: 1920,
      h: 1080,
      fit: 'scale',
    }),
    'imageVariant.horizontal-2x': sdkUtil.objectQueryString({
      w: 2560,
      h: 1440,
      fit: 'scale',
    }),
    'imageVariant.vertical': sdkUtil.objectQueryString({
      w: 1080,
      h: 1920,
      fit: 'scale',
    }),
    'imageVariant.vertical-2x': sdkUtil.objectQueryString({
      w: 1440,
      h: 2560,
      fit: 'scale',
    }),
    'imageVariant.portrait45': sdkUtil.objectQueryString({
      w: 1080,
      h: 1350,
      fit: 'scale',
    }),
    'imageVariant.portrait45-2x': sdkUtil.objectQueryString({
      w: 1440,
      h: 1800,
      fit: 'scale',
    }),
    'imageVariant.portrait34': sdkUtil.objectQueryString({
      w: 1080,
      h: 1440,
      fit: 'scale',
    }),
    'imageVariant.portrait34-2x': sdkUtil.objectQueryString({
      w: 1440,
      h: 1920,
      fit: 'scale',
    }),
    'imageVariant.square': sdkUtil.objectQueryString({
      w: 1080,
      h: 1080,
      fit: 'scale',
    }),
    'imageVariant.square2x': sdkUtil.objectQueryString({
      w: 1440,
      h: 1440,
      fit: 'scale',
    }),
  };

  return Promise.all([dispatch(showPortfolioWork(payload)), dispatch(fetchCurrentUser())])
    .then(response => {
      return response;
    })
    .catch(e => {
      throw e;
    });
};
